/**
 * outboxWeek — the pure "what should go out this week" summariser.
 *
 * (Lives beside the other pure email/sequence modules in src/lib/crm —
 * emailRecipes, sequenceRunner, emailMarketingAnalytics — because those are
 * its inputs and there is no separate src/lib/email package.)
 *
 * Email Marketing used to lead with its machinery (recipes, campaign
 * builder, analytics, autopilot). Operators kept saying it "looks
 * complicated". This module answers the ONE question that actually matters
 * on a Monday morning:
 *
 *   What email is due to go out this week, and what do I do about it?
 *
 * It folds three real, already-governed sources into three honest groups:
 *
 *   1. Sequence emails due — from the pure due-step engine
 *      (src/lib/crm/sequenceRunner.ts `dueSteps`), grouped by sequence.
 *   2. Weekly customer updates due — the `send_customer_update` proposals
 *      the weekly-update agent composes from real workspace data.
 *   3. Drafts awaiting your approval — email send diffs already sitting
 *      pending in the approvals inbox.
 *
 * Every group carries a count, the first few recipient names, and exactly
 * ONE action reference (approve / review / enroll) the hero can wire a chip
 * to. Nothing here reads a clock, a store, or sends anything — it is a pure
 * fold over shapes the caller derives from live data.
 */

/** How many recipient names each group surfaces before "+N more". */
export const MAX_PREVIEW_NAMES = 3;

/** The single governed action a hero card offers. */
export type OutboxActionKind = 'approve' | 'review' | 'enroll';

/** One due sequence step, flattened for the summary. */
export interface DueSequenceStepInput {
  /** Sequence this step belongs to — groups the summary. */
  sequenceId: string;
  /** Human-readable sequence name (falls back to id if blank). */
  sequenceName?: string;
  /** Recipient display name for the preview list. */
  recipientName: string;
}

/** One weekly customer update ready to compose/send. */
export interface WeeklyUpdateDueInput {
  /** Customer/person id — de-dupes one update per recipient. */
  personId: string;
  /** Recipient display name for the preview list. */
  recipientName: string;
}

/** One email draft (send diff) sitting pending in the approvals inbox. */
export interface PendingEmailApprovalInput {
  /** Approval row id — the action ref points at it for "approve". */
  id: string;
  /** Recipient display name for the preview list. */
  recipientName: string;
}

export interface WhatGoesOutInput {
  dueSequenceSteps: DueSequenceStepInput[];
  weeklyUpdatesDue: WeeklyUpdateDueInput[];
  pendingEmailApprovals: PendingEmailApprovalInput[];
}

/** The single action a hero card wires its chip to. */
export interface OutboxActionRef {
  kind: OutboxActionKind;
  /** Human label for the chip, e.g. "Review sequences". */
  label: string;
  /**
   * A stable target the UI can navigate/scroll to. Section keys
   * ('sequences' | 'approvals') or a specific approval id.
   */
  target: string;
}

/** One rendered summary group (sequences / updates / drafts). */
export interface OutboxGroup {
  /** Stable machine key for the group. */
  key: 'sequences' | 'weekly-updates' | 'approvals';
  /** Group heading, e.g. "3 sequence emails due". */
  title: string;
  /** Total items folded into this group. */
  count: number;
  /** First few recipient names (deduped, capped at MAX_PREVIEW_NAMES). */
  previewNames: string[];
  /** Count of recipients beyond the preview (0 when all shown). */
  moreCount: number;
  /** Per-sequence breakdown — only populated for the sequences group. */
  bySequence: Array<{ sequenceId: string; sequenceName: string; count: number }>;
  /** The one governed action this group offers. */
  action: OutboxActionRef;
}

export interface WhatGoesOutSummary {
  /** All groups with count > 0, in fixed priority order. */
  groups: OutboxGroup[];
  /** Grand total across all groups. */
  total: number;
  /** True when nothing is due this week — the honest empty hero. */
  isEmpty: boolean;
}

/** Deduped, capped preview list + the leftover count. */
function preview(names: string[]): { previewNames: string[]; moreCount: number } {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const raw of names) {
    const name = raw.trim() === '' ? 'Unnamed recipient' : raw.trim();
    if (seen.has(name)) continue;
    seen.add(name);
    unique.push(name);
  }
  return {
    previewNames: unique.slice(0, MAX_PREVIEW_NAMES),
    moreCount: Math.max(0, unique.length - MAX_PREVIEW_NAMES),
  };
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/**
 * Fold the three real sources into a grouped, action-tagged summary of
 * what should go out this week. Pure: no IO, inputs untouched. Groups with
 * zero items are dropped; when all three are empty the summary is honestly
 * empty and the caller renders the "nothing due" hero.
 */
export function whatGoesOutThisWeek(input: WhatGoesOutInput): WhatGoesOutSummary {
  const groups: OutboxGroup[] = [];

  // --- 1 · Sequence emails due, grouped by sequence ------------------------
  const steps = input.dueSequenceSteps ?? [];
  if (steps.length > 0) {
    const bySeq = new Map<string, { name: string; count: number }>();
    for (const s of steps) {
      const name = (s.sequenceName ?? '').trim() || s.sequenceId;
      const entry = bySeq.get(s.sequenceId) ?? { name, count: 0 };
      entry.count += 1;
      bySeq.set(s.sequenceId, entry);
    }
    const bySequence = [...bySeq.entries()]
      .map(([sequenceId, v]) => ({ sequenceId, sequenceName: v.name, count: v.count }))
      .sort((a, b) => b.count - a.count || a.sequenceName.localeCompare(b.sequenceName));
    const { previewNames, moreCount } = preview(steps.map((s) => s.recipientName));
    groups.push({
      key: 'sequences',
      title: `${steps.length} sequence ${plural(steps.length, 'email', 'emails')} due`,
      count: steps.length,
      previewNames,
      moreCount,
      bySequence,
      action: { kind: 'review', label: 'Review sequences', target: 'sequences' },
    });
  }

  // --- 2 · Weekly customer updates due -------------------------------------
  const updates = input.weeklyUpdatesDue ?? [];
  if (updates.length > 0) {
    const { previewNames, moreCount } = preview(updates.map((u) => u.recipientName));
    groups.push({
      key: 'weekly-updates',
      title: `${updates.length} weekly customer ${plural(updates.length, 'update', 'updates')} due`,
      count: updates.length,
      previewNames,
      moreCount,
      bySequence: [],
      action: { kind: 'review', label: 'Review updates', target: 'approvals' },
    });
  }

  // --- 3 · Drafts awaiting your approval -----------------------------------
  const pending = input.pendingEmailApprovals ?? [];
  if (pending.length > 0) {
    const { previewNames, moreCount } = preview(pending.map((p) => p.recipientName));
    groups.push({
      key: 'approvals',
      title: `${pending.length} ${plural(pending.length, 'draft', 'drafts')} awaiting your approval`,
      count: pending.length,
      previewNames,
      moreCount,
      bySequence: [],
      action: { kind: 'approve', label: 'Open approvals', target: 'approvals' },
    });
  }

  const total = groups.reduce((sum, g) => sum + g.count, 0);
  return { groups, total, isEmpty: groups.length === 0 };
}
